import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useLayout } from '../context/LayoutContext';
import { useTranslation } from 'react-i18next';

const NavbarContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  z-index: 1000;
  backdrop-filter: blur(8px);
  background: ${({ theme }) => `${theme.colors.background}99`};
  transition: all 0.3s ease;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Logo = styled.a`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  text-decoration: none;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled(motion.div)`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    width: 80%;
    max-width: 300px;
    height: 100vh;
    background: ${({ theme }) => theme.colors.background};
    z-index: 999;
    padding-top: 5rem;
    box-shadow: 5px 0 15px rgba(0, 0, 0, 0.3);
    border-right: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const MobileMenuOverlay = styled(motion.div)`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(3px);
    z-index: 998;
  }
`;

const MobileMenuLink = styled(motion.a)`
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-size: 1.2rem;
  padding: 1rem 2rem;
  transition: all 0.3s ease;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: ${({ theme }) => theme.colors.primary};
    transform: scaleY(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.primary};
    
    &:before {
      transform: scaleY(1);
    }
  }
`;

const HamburgerButton = styled(motion.button)`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    width: 2rem;
    height: 2rem;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    z-index: 1000;

    span {
      width: 2rem;
      height: 0.25rem;
      background: ${({ theme }) => theme.colors.text};
      border-radius: 10px;
      transition: all 0.3s linear;
      position: relative;
      transform-origin: 1px;
    }
  }
`;

const NavLinkItem = styled(motion.div)<{ active: boolean }>`
  position: relative;
  
  a {
    color: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.text};
    text-decoration: none;
    font-weight: ${({ active }) => active ? '600' : '400'};
    font-size: 1rem;
    transition: all 0.2s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const LanguageSwitcher = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-left: 2rem;
  position: relative;
  
  @media (max-width: 768px) {
    margin-left: 1rem;
  }
`;

const LanguageButton = styled(motion.button)`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text};
  padding: 0.3rem 0.6rem;
  font-size: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLight};
    border-color: ${({ theme }) => theme.colors.primary}50;
  }
  
  svg {
    width: 14px;
    height: 14px;
    margin-left: 5px;
    fill: ${({ theme }) => theme.colors.text};
  }
`;

const LanguageOptions = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 120px;
`;

const LanguageOption = styled(motion.button)`
  background: transparent;
  border: none;
  padding: 0.6rem 1rem;
  text-align: left;
  color: ${({ theme }) => theme.colors.text};
  width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.9rem;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.primary};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const MobileLanguageSwitcher = styled(motion.div)`
  padding: 1rem 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: auto;
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const MobileLanguageButton = styled(motion.button)<{ active: boolean }>`
  background: ${({ active, theme }) => active ? theme.colors.primary : 'transparent'};
  border: 1px solid ${({ active, theme }) => active ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  color: ${({ active, theme }) => active ? 'white' : theme.colors.text};
  padding: 0.5rem 1rem;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ active, theme }) => active ? theme.colors.primary : theme.colors.backgroundLight};
  }
`;

interface NavbarProps {
  scrolled?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ scrolled = false }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(scrolled);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const location = useLocation();
  
  // Utilisation du contexte mis à jour
  const { isMobile } = useLayout();
  
  // Force le rendu du contenu mobile/desktop au changement de route
  useEffect(() => {
    // Suppression du log de débogage
  }, [location.pathname, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isLanguageMenuOpen) {
        const target = event.target as HTMLElement;
        const languageSwitcher = document.getElementById('language-switcher');
        
        if (languageSwitcher && !languageSwitcher.contains(target)) {
          setIsLanguageMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageMenuOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageMenuOpen(false);
  };

  const navItems = [
    { title: t('navbar:menu.home'), path: '/' },
    { title: t('navbar:menu.skills'), path: '/competences' },
    { title: t('navbar:menu.career'), path: '/parcours' },
    { title: t('navbar:menu.education'), path: '/formations' },
    { title: t('navbar:menu.portfolio'), path: '/portfolio' },
    { title: t('navbar:menu.resume'), path: '/cv' },
    { title: t('navbar:menu.contact'), path: '/contact' }
  ];

  // Animation variants for the mobile menu
  const menuVariants = {
    closed: { 
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    },
    open: { 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30,
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  // Animation variants for mobile menu items
  const menuItemVariants = {
    closed: { 
      x: -20,
      opacity: 0 
    },
    open: { 
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300
      }
    }
  };

  // Animation variants for overlay
  const overlayVariants = {
    closed: { 
      opacity: 0,
      transition: {
        delay: 0.2
      }
    },
    open: { 
      opacity: 1 
    }
  };

  // Animation variants for language menu
  const languageMenuVariants = {
    hidden: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: {
        duration: 0.2
      }
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <NavbarContainer style={{ backgroundColor: hasScrolled ? 'rgba(20, 27, 37, 0.95)' : 'transparent' }}>
      <Logo href="/">
        {t('navbar:logo')}
      </Logo>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <NavLinks style={{ display: isMobile ? 'none' : 'flex' }}>
          {navItems.map((item) => (
            <NavLinkItem
              key={item.path}
              active={location.pathname === item.path}
              whileHover={{ y: -2 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <a href={item.path}>{item.title}</a>
            </NavLinkItem>
          ))}
        </NavLinks>

        {!isMobile && (
          <LanguageSwitcher id="language-switcher">
            <LanguageButton onClick={toggleLanguageMenu}>
              {i18n.language === 'fr' ? 'FR' : 'EN'}
              <svg viewBox="0 0 24 24">
                <path d="M7 10l5 5 5-5z"/>
              </svg>
            </LanguageButton>
            
            <AnimatePresence>
              {isLanguageMenuOpen && (
                <LanguageOptions
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={languageMenuVariants}
                >
                  <LanguageOption 
                    onClick={() => changeLanguage('fr')}
                    whileHover={{ x: 3 }}
                  >
                    Français
                  </LanguageOption>
                  <LanguageOption 
                    onClick={() => changeLanguage('en')}
                    whileHover={{ x: 3 }}
                  >
                    English
                  </LanguageOption>
                </LanguageOptions>
              )}
            </AnimatePresence>
          </LanguageSwitcher>
        )}

        <HamburgerButton
          style={{ display: isMobile ? 'flex' : 'none' }}
          onClick={toggleMenu}
          animate={isOpen ? "open" : "closed"}
        >
          <motion.span
            variants={{
              closed: { rotate: 0 },
              open: { rotate: 45, y: 10 }
            }}
          />
          <motion.span
            variants={{
              closed: { opacity: 1 },
              open: { opacity: 0 }
            }}
          />
          <motion.span
            variants={{
              closed: { rotate: 0 },
              open: { rotate: -45, y: -10 }
            }}
          />
        </HamburgerButton>
      </div>

      <AnimatePresence>
        {isOpen && (
          <MobileMenuOverlay
            initial="closed"
            animate="open"
            exit="closed"
            variants={overlayVariants}
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <MobileMenu
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            {navItems.map((item) => (
              <MobileMenuLink
                key={item.path}
                href={item.path}
                onClick={() => setIsOpen(false)}
                variants={menuItemVariants}
                style={{ fontWeight: location.pathname === item.path ? 600 : 400 }}
              >
                {item.title}
              </MobileMenuLink>
            ))}

            {/* Sélecteur de langue mobile */}
            <MobileLanguageSwitcher>
              <MobileLanguageButton 
                active={i18n.language === 'fr'} 
                onClick={() => changeLanguage('fr')}
                whileTap={{ scale: 0.95 }}
              >
                Français
              </MobileLanguageButton>
              <MobileLanguageButton 
                active={i18n.language === 'en'} 
                onClick={() => changeLanguage('en')}
                whileTap={{ scale: 0.95 }}
              >
                English
              </MobileLanguageButton>
            </MobileLanguageSwitcher>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

export default Navbar;